/*
 * Copyright (c) CIRROM 2025.
 */

import { API } from "./api";

export interface CirromMonitorItem {
  id: number;
  first_name: string;
  last_name: string;
  job_title: string;
  company: string;
  location: string;
  linkedin_url: string;
  first_name_probability: number;
  last_name_probability: number;
  job_title_probability: number;
  company_probability: number;
  probability: number;
}

export interface CirromMonitorCRMRecord {
  record_id: number;
  first_name: string;
  last_name: string;
  job_title: string;
  company: string;
  location: string;
}

export interface CirromMonitorGroup {
  crm: CirromMonitorCRMRecord;
  cirrom_monitor: CirromMonitorItem[];
}

export interface ContactMonitorStats {
  pending_contacts: number;
  linked_contacts: number;
  monitored_contacts: number;
  total_contacts: number;
  data_enrichments: number;
  job_movers: number;
  location: number;
  company: number;
  job_title: number;
}

export interface CirromMonitorSuggestedChange {
  id: number;
  record_id: number;
  first_name: string;
  last_name: string;
  crm_id: string;
  field: string;
  new_value: string;
  old_value: string;
  job_title: string;
  company: string;
}

export interface CirromMonitorCRMComparison {
  crm_results: {
    first_name: string;
    last_name: string;
    job_title: string;
    suffix: string;
    location: string;
    crm_contact_id: string;
    middle_name?: string;
    goes_by?: string;
    company?: string;
    primary_email?: string;
    city?: string;
    country?: string;
    contact_source?: string;
    salutation?: string;
  };
  monitor_results: Record<string, { value: string; id: number }[]>;
  pending_results: {
    status: string;
    first_name: string;
    last_name: string;
    job_title: string;
    company: string;
    location: string;
    monitor_dao_id: string;
    linkedin_url: string;
  }[];
  accepted_results: {
    id: number;
    field: string;
    old_value: string;
    new_value: string;
    change_status: string;
    change_type: string;
    probability: number;
    source: string;
  }[];
}

export interface MonitorCompanyMovers {
  record_id: number;
  name: string;
  old_company: string;
  new_company: string;
  date?: string;
}

export interface MonitorStatusSplit {
  matched: number;
  suggested: number;
  monitored: number;
}

export interface MonitorJobTitleChange {
  record_id: number;
  name: string;
  old_job_title: string;
  new_job_title: string;
  date?: string;
}

export interface ContactMonitorChangesMadeStats {
  total: number;
  name: number;
  job_title: number;
  company_movers: number;
  city: number;
  country: number;
}

export const getCirromMonitorStatsForProjectOrSystem = async (
  integratedSystemId?: number,
  dataProjectId?: number
): Promise<ContactMonitorStats> => {
  const response = await API.get("/cirrom-monitor-stats", {
    params: {
      data_project_id: dataProjectId,
      integrated_system_id: integratedSystemId,
    },
  });
  return response.data;
};

export interface ContactMonitorFilters {
  firstNamesAreIdentical: boolean;
  firstNamesAreSimilar: boolean;
  lastNamesAreIdentical: boolean;
  companyNamesAreIdentical: boolean;
  companyNamesAreNotIdentical: boolean;
  cityIsIdentical: boolean;
  countryIsIdentical: boolean;
  cityIsNotIdentical: boolean;
  countryIsNotIdentical: boolean;
  includeNotFound: boolean;
  notFoundOnly: boolean;
}

export const getMonitorResultsForDataProject = async (
  systemOrProjectId: string,
  statusFilter: string,
  additionalFilters?: ContactMonitorFilters,
  isDataProject: boolean = true
): Promise<CirromMonitorGroup[]> => {
  const response = await API.get(`/cirrom-monitor/`, {
    params: {
      data_project_id: isDataProject ? systemOrProjectId : undefined,
      integrated_system_id: isDataProject ? undefined : systemOrProjectId,
      status_filter: statusFilter,
      first_names_are_identical: additionalFilters?.firstNamesAreIdentical,
      first_names_are_similar: additionalFilters?.firstNamesAreSimilar,
      last_names_are_identical: additionalFilters?.lastNamesAreIdentical,
      company_is_identical: additionalFilters?.companyNamesAreIdentical,
      company_is_not_identical: additionalFilters?.companyNamesAreNotIdentical,
      city_is_identical: additionalFilters?.cityIsIdentical,
      country_is_identical: additionalFilters?.countryIsIdentical,
      city_is_not_identical: additionalFilters?.cityIsNotIdentical,
      country_is_not_identical: additionalFilters?.countryIsNotIdentical,
      include_not_found: additionalFilters?.includeNotFound,
      not_found_only: additionalFilters?.notFoundOnly,
    },
  });
  return response.data;
};

export const getMonitorSuggestedChangesForDataProject = async (
  dataProjectId: string,
  dataPoint: string
): Promise<CirromMonitorSuggestedChange[]> => {
  const response = await API.get("/cirrom-monitor-suggested-changes/", {
    params: { data_project_id: dataProjectId, data_point: dataPoint },
  });
  return response.data;
};

export const getMonitorSuggestedChangesSummaryForDataProject = async (
  dataProjectId: string,
  dataPoint: string
): Promise<CirromMonitorSuggestedChange[]> => {
  const response = await API.get("/cirrom-monitor-suggested-changes-summary/", {
    params: { data_project_id: dataProjectId, data_point: dataPoint },
  });
  return response.data;
};

export const rejectCirromMonitorChange = async (
  dataProjectId: number,
  changeIds: number[]
): Promise<any> => {
  const response = await API.post(`/cirrom-monitor/reject`, {
    data_project_id: dataProjectId,
    monitor_suggestion_ids: changeIds,
  });
  return response.data;
};

export const revertCirromMonitorChange = async (
  dataProjectId: number,
  changeIds: number[]
): Promise<any> => {
  const response = await API.post(`/cirrom-monitor/revert`, {
    data_project_id: dataProjectId,
    monitor_suggestion_ids: changeIds,
  });
  return response.data;
};

export const acceptCirromMonitorChange = async (
  dataProjectId: number,
  changeIds: number[],
  newStatus: string
): Promise<any> => {
  const response = await API.post(`/cirrom-monitor/accept`, {
    data_project_id: dataProjectId,
    monitor_suggestion_ids: changeIds,
    new_status: newStatus,
  });
  return response.data;
};

export const runCirromMonitorForDataProject = async (
  dataProjectId: string
): Promise<any> => {
  const response = await API.post(`/cirrom-monitor/run/${dataProjectId}`);
  return response.data;
};

export const runCirromMonitorForDataProjectContact = async (
  dataProjectId: string,
  recordId: number
) => {
  const response = await API.post(
    `/cirrom-monitor/run/${dataProjectId}/${recordId}`
  );
  return response.data;
};

export const getMonitorCRMComparison = async (
  recordId: number,
  integratedSystemId: number
): Promise<CirromMonitorCRMComparison> => {
  const response = await API.get("/cirrom-monitor-crm-comparison", {
    params: {
      record_id: recordId,
      integrated_system_id: integratedSystemId,
    },
  });
  return response.data;
};

export const acceptManualCirromMonitorLink = async (
  recordId: number,
  linkedinUrl: string,
  dataProjectId?: number,
  integratedSystemId?: number
): Promise<any> => {
  const response = await API.post(`/cirrom-monitor/accept-manual`, {
    record_id: recordId,
    data_project_id: dataProjectId,
    integrated_system_id: integratedSystemId,
    linkedin_url: linkedinUrl,
  });
  return response.data;
};

export const rejectCirromMonitorSuggestedChanges = async (
  changeIds: number[]
): Promise<any> => {
  const response = await API.post(`/cirrom-monitor-suggested-changes/reject`, {
    suggestion_ids: changeIds,
  });
  return response.data;
};

export const acceptCirromMonitorSuggestedChange = async (
  suggestions: {
    record_id: number;
    integrated_system_id: number;
    suggestion_id: number;
  }[]
): Promise<any> => {
  const response = await API.post(
    `/cirrom-monitor-suggested-changes/accept`,
    suggestions
  );
  return response.data;
};

export const acceptCirromMonitorChangeWithCompany = async (
  recordId: number,
  companyName: string,
  crmAccountId: string,
  integratedSystemId: number
): Promise<any> => {
  const response = await API.post(
    `/cirrom-monitor-suggested-changes/accept-with-company`,
    {
      record_id: recordId,
      company_name: companyName,
      crm_account_id: crmAccountId,
      integrated_system_id: integratedSystemId,
    }
  );
  return response.data;
};

export const acceptCompanyAddressLink = async (
  recordId: number,
  integratedSystemId: number,
  addressId: string
): Promise<any> => {
  const response = await API.post(
    `/cirrom-monitor-suggested-changes/accept-company-address-link`,
    {
      record_id: recordId,
      integrated_system_id: integratedSystemId,
      address_id: addressId,
    }
  );
  return response.data;
};

export const getCompanyMoversForProjectOrSystem = async (
  dataProjectId?: number,
  integratedSystemId?: number
): Promise<MonitorCompanyMovers> => {
  const response = await API.get("/cirrom-monitor-recent-company-movers", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};

export const getMonitorStatusSplitForProjectOrSystem = async (
  dataProjectId?: number,
  integratedSystemId?: number
): Promise<MonitorStatusSplit> => {
  const response = await API.get("/cirrom-monitor-contact-stats", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};

export interface EmailAndAddressSuggestion {
  suggested_email: string;
  addresses: {
    address_id: string;
    street: string;
    city: string;
    stateorprovince: string;
    country: string;
    zipcode: string;
  }[];
}

export const getEmailAndAddressSuggestions = async (
  integratedSystemId: number,
  recordId: number,
  crmAccountId: string
): Promise<EmailAndAddressSuggestion> => {
  const response = await API.get("/get-email-and-address-suggestions", {
    params: {
      integrated_system_id: integratedSystemId,
      record_id: recordId,
      crm_account_id: crmAccountId,
    },
  });
  return response.data;
};

export const approveAdHocMonitorChange = async (
  recordId: number,
  integratedSystemId: number,
  fieldName: string,
  newValue?: string,
  oldValue?: string
): Promise<any> => {
  const response = await API.post(
    `/cirrom-monitor-suggested-changes/approve-adhoc`,
    {
      record_id: recordId,
      integrated_system_id: integratedSystemId,
      field_name: fieldName,
      new_value: newValue,
      old_value: oldValue,
    }
  );
  return response.data;
};

export const getJobTitleChangesForProjectOrSystem = async (
  dataProjectId?: number,
  integratedSystemId?: number
): Promise<MonitorJobTitleChange[]> => {
  const response = await API.get("/cirrom-monitor-recent-job-title-changes", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};

export const getChangesMadeStatsForProjectOrSystem = async (
  integratedSystemId?: number,
  dataProjectId?: number
): Promise<ContactMonitorChangesMadeStats> => {
  const response = await API.get("/cirrom-monitor-changes-made-stats", {
    params: {
      data_project_id: dataProjectId,
      integrated_system_id: integratedSystemId,
    },
  });
  return response.data;
};
